import React from "react";
import Helmet from "react-helmet";
import { withPrefix } from "gatsby";
import { Node as ReactNode } from "react";
import { useSiteMetadata } from "../../hooks";
import styles from "./Layout.module.scss";

type Props = {
  title: string,
  description?: string,
  socialImage?: string,
};

const getSchemaOrgJSONLD = ({
  isBlogPost,
  url,
  title,
  image,
  description,
  datePublished,
  featuredImage,
}) => {
  const schemaOrgJSONLD = [
    {
      "@context": "http://schema.org",
      "@type": "WebSite",
      url: "https://www.codevscolor.com",
      name: title,
      alternateName: title,
    },
  ];

  return isBlogPost
    ? [
        ...schemaOrgJSONLD,
        {
          "@context": "http://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              item: {
                "@id": url,
                name: title,
                image: featuredImage,
              },
            },
          ],
        },
        {
          "@context": "http://schema.org",
          "@type": "BlogPosting",
          url,
          name: title,
          alternateName: title,
          headline: title,
          image: {
            "@type": "ImageObject",
            url: featuredImage,
          },
          description,
          author: {
            "@type": "Person",
            name: "N Kaushik",
          },
          publisher: {
            "@type": "Organization",
            url: "https://www.codevscolor.com",
            logo: {
              "@type": "ImageObject",
              url: image,
            },
            name: "codevscolor",
          },
          mainEntityOfPage: {
            "@type": "WebSite",
            "@id": "https://www.codevscolor.com",
          },
          datePublished,
        },
      ]
    : schemaOrgJSONLD;
};

const Layout: React.FC<Props> = ({
  children,
  title,
  description,
  socialImage,
  isBlogPost,
  datePublished,
  postUrl,
  featuredImage,
  noIndex
}) => {
  const { author, url } = useSiteMetadata();
  const metaImage = featuredImage != null ? featuredImage : socialImage;
  const metaImageUrl = metaImage;
  const socialImg = 'https://www.codevscolor.com/codevscolor-logo512.png'

  const schemaOrgJSONLD = getSchemaOrgJSONLD({
    isBlogPost,
    url: postUrl,
    title,
    image: socialImg,
    description,
    datePublished,
    featuredImage,
  });

  return (
    <div className={styles.layout}>
      <Helmet>
        <html lang="en" />
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="image" content={metaImageUrl} />

        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>

        {isBlogPost ? <meta property="og:type" content="article" /> : null}
        {noIndex ? <meta name="robots" content="noindex"/> : null}
        <meta property="og:title" content={title} />
        <meta property="og:url" content={postUrl} />
        <meta property="og:site_name" content="CodeVsColor" />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={metaImageUrl} />
        <meta property="og:locale" content="en_GB" />

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:creator" content="codevscolor" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={metaImageUrl} />
      </Helmet>
      {children}
    </div>
  );
};

export default Layout;
